import cn from 'classnames'
import React, { useMemo } from 'react'

import { isMixed } from 'components/uiKit/KitUtils'
import { t } from 'services/Translation'
import { testPropsEl } from 'utils/test/qaData'

import {
  useSelectSize,
  useSelectStyleType,
  useSelectContext,
  useSelectHandlers,
} from '../SelectContext'
import SelectIcon from '../SelectIcon'
import SelectInput from '../SelectInput'
import SelectRootItem from '../SelectRootItem'
import { isEmptyValue, mapValue } from '../SelectUtils'
import * as s from './SelectRoot.module.scss'

const SelectRoot = React.forwardRef<HTMLDivElement>((_props, ref) => {
  const size = useSelectSize()
  const minWidth = useSelectContext((c) => c.minWidth)
  const width = useSelectContext((c) => c.width)
  const styleType = useSelectStyleType()
  const fluid = useSelectContext((c) => c.fluid)
  const rawValue = useSelectContext((c) => c.value)
  const value = isMixed(rawValue) ? undefined : rawValue
  const disabled = useSelectContext((c) => c.disabled)
  const readOnly = useSelectContext((c) => c.readOnly)
  const name = useSelectContext((c) => c.name)
  const inputable = useSelectContext((c) => c.inputable)
  const map = useSelectContext((c) => c.map)
  const error = useSelectContext((c) => c.error)
  const inputValue = useSelectContext((c) => c.inputValue)
  const focus = useSelectContext((c) => c.focus)
  const placeholder = useSelectContext((c) => c.placeholder)
  const isMultiple = useSelectContext((c) => c.isMultiple)
  const { handleMouseUp } = useSelectHandlers()

  const items = useMemo(() => mapValue(map, value), [value, map])

  const cnRoot = cn(s.root, s[size], s[styleType], {
    [s.disabled]: disabled,
    [s.inputable]: inputable,
    [s.fluid]: fluid,
    [s.focus]: focus && !readOnly,
    [s.error]: error,
    [s.single]: !isMultiple,
  })
  const cnPlaceholder = cn(s.placeholder, s[styleType], s[size])
  const cnValue = cn(s.value, s[size], s[styleType])

  return (
    <div
      {...testPropsEl(
        'select',
        { name, value },
        { disabled, label: String(items.map((item) => item.textLabel || item.label)) },
      )}
      className={cnRoot}
      onMouseUp={handleMouseUp}
      ref={ref}
      style={{ width, minWidth }}
    >
      <div className={cnValue}>
        {items.map((item) => (
          <SelectRootItem item={item} key={String(item.value)} />
        ))}
        <SelectInput />
        {isEmptyValue(value) && !inputValue && (
          <div className={cnPlaceholder}>{isMixed(rawValue) ? t('input.mixed') : placeholder}</div>
        )}
      </div>
      <SelectIcon name={name} />
    </div>
  )
})

SelectRoot.displayName = 'SelectRoot'

export default SelectRoot
